// https://github.com/modusign/modusign-client/blob/82f1ecc37cd641f146779a41cf4935a17b5e3f57/app/src/components/AttachmentListModal/index.tsx

import { faPaperclipVertical } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, Text, Tooltip } from '@modusign/design-system';
import { useTranslation } from 'react-i18next';
import { DeepReadonly } from 'ts-essentials';

import DesktopAttachmentList from '@app/components/AttachmentListModal/DesktopAttachmentList';
import MobileAttachmentList from '@app/components/AttachmentListModal/MobileAttachmentList';
import {
  WindowModal,
  WindowModalFooter,
  WindowModalHeader,
} from '@app/components/Modal/WindowModal';
import { AttachmentInformation } from '@app/features/apply-react-signing/libs/getAttachedAttachments';
import useResponsiveValue from '@app/hooks/useResponsiveValue';

interface AttachmentListModalProps {
  isOpen: boolean;
  isDownloading: boolean;
  isDocumentCompleted?: boolean;
  attachments: DeepReadonly<AttachmentInformation[]>;
  enableDownload: boolean;
  onOpenAttachmentClick: (attachment: AttachmentInformation, index: number) => void;
  onDownloadAttachmentClick: (attachment: AttachmentInformation, index: number) => void;
  onDownloadAttachmentBundleClick: (type: 'REQUESTER' | 'SIGNER' | 'ALL') => void;
  onClose: () => void;
}

export default function AttachmentListModal({
  isOpen,
  isDownloading,
  isDocumentCompleted,
  attachments,
  enableDownload,
  onDownloadAttachmentBundleClick,
  onDownloadAttachmentClick,
  onOpenAttachmentClick,
  onClose,
}: AttachmentListModalProps) {
  const { t } = useTranslation();

  const AttachmentList = useResponsiveValue([MobileAttachmentList, DesktopAttachmentList]);
  return (
    <WindowModal isOpen={isOpen} maxWidth="md" onRequestClose={onClose}>
      <WindowModalHeader onClose={onClose}>
        <Text
          as={FontAwesomeIcon}
          className="fa-flip-horizontal"
          fontSize="18px"
          icon={faPaperclipVertical}
          mr="11px"
        />
        {t('DocumentAttachmentListModal-attachment')}
      </WindowModalHeader>
      <AttachmentList
        attachments={attachments}
        enableDownload={enableDownload}
        isDocumentCompleted={isDocumentCompleted}
        isDownloading={isDownloading}
        onDownloadAttachmentBundleClick={onDownloadAttachmentBundleClick}
        onDownloadAttachmentClick={onDownloadAttachmentClick}
        onOpenAttachmentClick={onOpenAttachmentClick}
      />
      <WindowModalFooter display="flex" justifyContent="flex-end">
        <Tooltip
          content="모든 서명이 완료되면 전체 다운로드가 가능합니다."
          disabled={isDocumentCompleted}
          placement="left"
        >
          <Button
            disabled={!isDocumentCompleted}
            isLoading={isDownloading}
            variant="primary"
            onClick={() => onDownloadAttachmentBundleClick('ALL')}
          >
            전체 다운로드
          </Button>
        </Tooltip>
      </WindowModalFooter>
    </WindowModal>
  );
}

export interface ModalContentProps {
  attachments: DeepReadonly<AttachmentInformation[]>;
  enableDownload: boolean;
  isDocumentCompleted?: boolean;
  isDownloading: boolean;
  onOpenAttachmentClick: (attachment: DeepReadonly<AttachmentInformation>, index: number) => void;
  onDownloadAttachmentClick: (
    attachment: DeepReadonly<AttachmentInformation>,
    index: number
  ) => void;
  onDownloadAttachmentBundleClick: (type: 'REQUESTER' | 'SIGNER' | 'ALL') => void;
}
